import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

export async function adminFetch(path, options = {}) {
  const headers = { ...(options.headers || {}) };
  const opts = { ...options, headers, credentials: "include" };
  if (opts.json !== undefined) {
    headers["Content-Type"] = "application/json";
    opts.body = JSON.stringify(opts.json);
    delete opts.json;
  }
  const res = await fetch(`${API}${path}`, opts);
  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok) {
    const detail = data && data.detail;
    const msg = typeof detail === "string" && detail
      ? detail
      : res.status === 401
      ? "Your session has expired. Please sign in again."
      : "We couldn't save your changes. Please review your entries and try again.";
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return data;
}

export function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(undefined); // undefined = loading, null = signed out

  const authSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++authSeq.current;
    try {
      const me = await adminFetch("/api/admin/me");
      if (seq === authSeq.current) setAdmin(me);
    } catch {
      if (seq === authSeq.current) setAdmin(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const login = async (username, password) => {
    const data = await adminFetch("/api/admin/login", { method: "POST", json: { username, password } });
    authSeq.current += 1; // invalidate any in-flight /me check
    setAdmin(data.admin);
    return data.admin;
  };

  const logout = async () => {
    setAdmin(null);
    try {
      await adminFetch("/api/admin/logout", { method: "POST" });
    } catch {
      /* ignore */
    }
  };

  return (
    <AuthContext.Provider value={{ admin, setAdmin, login, logout, refresh: load }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
